import * as React from 'react';
import { Mic, Loader2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { AudioRecorder } from 'components/ui/audio-recorder';
import { useTranscribeAudio } from 'features/transcription/api';
import { cn } from 'lib/utils';

export type InsertMode = 'replace' | 'append';

export interface AudioInputPopoverProps {
    onTranscriptionComplete: (text: string, mode: InsertMode) => void;
    postProcessing?: string | null;
    hasExistingText?: boolean;
    disabled?: boolean;
    className?: string;
}

export const AudioInputPopover = React.forwardRef<
    HTMLButtonElement,
    AudioInputPopoverProps
>(
    (
        {
            onTranscriptionComplete,
            postProcessing = null,
            hasExistingText = false,
            disabled = false,
            className,
        },
        ref,
    ) => {
        const [open, setOpen] = React.useState(false);
        const [insertMode, setInsertMode] =
            React.useState<InsertMode>('append');
        const [recordingComplete, setRecordingComplete] = React.useState(false);
        const [recorderKey, setRecorderKey] = React.useState(0);

        const { mutate: transcribe, isPending } = useTranscribeAudio({
            mutationConfig: {
                onSuccess: (data) => {
                    onTranscriptionComplete(data.transcription, insertMode);
                    setOpen(false);
                    setRecordingComplete(false);
                },
                onError: (error) => {
                    console.error('Transcription error:', error);
                    setRecordingComplete(false);
                },
            },
        });

        const handleRecordingComplete = (audioBlob: Blob) => {
            setRecordingComplete(true);
            transcribe({
                audio: audioBlob,
                post_processing: postProcessing,
            });
        };

        const handleCancel = () => {
            setOpen(false);
            setRecordingComplete(false);
        };

        const handleOpenChange = (newOpen: boolean) => {
            if (!isPending) {
                setOpen(newOpen);
                if (!newOpen) {
                    setRecordingComplete(false);
                    // Force remount of AudioRecorder to clean up resources
                    setRecorderKey((prev) => prev + 1);
                }
            }
        };

        return (
            <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                    <Button
                        ref={ref}
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={cn('h-8 w-8', className)}
                        disabled={disabled}
                        title="Voice input"
                    >
                        <Mic className="h-4 w-4" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-80"
                    align="end"
                    onInteractOutside={(e) => {
                        if (isPending) {
                            e.preventDefault();
                        }
                    }}
                >
                    <div className="space-y-4">
                        <div>
                            <h4 className="font-medium">Voice Input</h4>
                            <p className="text-sm text-muted-foreground">
                                Record your message
                            </p>
                        </div>

                        {isPending ? (
                            <div className="flex flex-col items-center justify-center space-y-3 py-8">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                <p className="text-sm text-muted-foreground">
                                    Transcribing audio...
                                </p>
                            </div>
                        ) : (
                            <>
                                <AudioRecorder
                                    key={recorderKey}
                                    onRecordingComplete={
                                        handleRecordingComplete
                                    }
                                    onCancel={handleCancel}
                                    autoStart={true}
                                    showControls={!recordingComplete}
                                />

                                {hasExistingText && !recordingComplete && (
                                    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                                        <p className="text-xs font-medium">
                                            Insert mode:
                                        </p>
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant={
                                                    insertMode === 'append'
                                                        ? 'default'
                                                        : 'outline'
                                                }
                                                size="sm"
                                                onClick={() =>
                                                    setInsertMode('append')
                                                }
                                                className="flex-1"
                                            >
                                                Append
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={
                                                    insertMode === 'replace'
                                                        ? 'default'
                                                        : 'outline'
                                                }
                                                size="sm"
                                                onClick={() =>
                                                    setInsertMode('replace')
                                                }
                                                className="flex-1"
                                            >
                                                Replace
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        );
    },
);

AudioInputPopover.displayName = 'AudioInputPopover';
